/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   รายละเอียดโปรเจ็กต์บ้าน   /support/project/view/:code
   ข้อมูลลูกค้า/ที่ดิน/สัญญา + แบบบ้าน/ไฟล์แนบ + ความคืบหน้าตามแผน + ทีม + RC + ใบเลือกวัสดุ
============================================================ */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { EditOutlined, EnvironmentOutlined, FormOutlined, PlusOutlined, StopOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ButtonBack } from "../../../components/button";
import StatusTag from "../../../components/ak/StatusTag";
import { ProjectService, SelectionService, apiError } from "../../../service/Ak.service";
import OptionService from "../../../service/Options.service";
import { usePermission } from "../../../store/context/permission-context";
import { fileUrl, formatMoney } from "../../../utils/util";
import ProjectFiles from "./ProjectFiles";
import ProjectRc from "./ProjectRc";

const opt = OptionService();
const d = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-");

export default function ProjectView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { can } = usePermission();
  const [p, setP] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [openTpl, setOpenTpl] = useState(false);
  const [tpl, setTpl] = useState();
  const [creating, setCreating] = useState(false);

  const load = () =>
    ProjectService.get(code)
      .then((res) => setP(res.data?.data))
      .catch((e) => message.error(apiError(e, "โหลดโปรเจ็กต์ไม่สำเร็จ")));

  useEffect(() => {
    load();
  }, [code]);

  const openNewSelection = () => {
    opt.templates().then((r) => {
      const list = r.data?.data || [];
      setTemplates(list);
      /* แนะนำ Template ของแบบบ้านนี้ก่อน */
      const match = list.find((t) => t.model_code && t.model_code === p?.model_code) || list.find((t) => !t.model_code);
      setTpl(match?.tpl_code);
      setOpenTpl(true);
    });
  };

  const createSelection = async () => {
    if (!tpl) return message.warning("เลือก Template ก่อน");
    setCreating(true);
    try {
      const res = await SelectionService.create({ project_code: code, tpl_code: tpl });
      navigate(`/support/selection/manage/${res.data?.data?.sel_code}`);
    } catch (e) {
      message.error(apiError(e, "สร้างใบเลือกวัสดุไม่สำเร็จ"));
    } finally {
      setCreating(false);
    }
  };

  const cancelProject = () =>
    Modal.confirm({
      title: "ยกเลิกโปรเจ็กต์นี้?",
      content: "สถานะจะเป็น ยกเลิก และไม่แสดงในรายการปกติ",
      okText: "ยกเลิกโปรเจ็กต์",
      okButtonProps: { danger: true },
      cancelText: "ไม่",
      onOk: () => ProjectService.remove(code).then(load).catch((e) => message.error(apiError(e))),
    });

  if (!p) return <Flex justify="center" style={{ padding: 80 }}><Spin /></Flex>;

  /* ความคืบหน้าตามเวลา (ยังไม่ใช่ความคืบหน้างานจริง — รอโมดูลแผนงาน/งวดงาน) */
  let timePct = null;
  let dayText = null;
  if (p.start_date && p.end_date) {
    const total = dayjs(p.end_date).diff(dayjs(p.start_date), "day") + 1;
    const passed = Math.min(total, Math.max(0, dayjs().startOf("day").diff(dayjs(p.start_date), "day") + 1));
    timePct = Math.round((passed / total) * 100);
    dayText = dayjs().isBefore(dayjs(p.start_date)) ? `เริ่มงานอีก ${dayjs(p.start_date).diff(dayjs().startOf("day"), "day")} วัน` : `วันที่ ${passed} จาก ${total} วัน`;
  }
  const activeSel = (p.selections || []).find((s) => s.status !== "ยกเลิก");
  const site = [p.site_address, p.site_subdistrict, p.site_district, p.site_province, p.site_zipcode].filter(Boolean).join(" ");

  return (
    <div>
      <Flex justify="space-between" align="center" className="mb-3" wrap="wrap" gap={8}>
        <div>
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {p.project_name} <StatusTag value={p.status} />
          </Typography.Title>
          <Typography.Text type="secondary">
            {p.project_code}
            {p.model_code ? ` · แบบ ${p.model_code} ${p.model_name || ""}` : ""}
          </Typography.Text>
        </div>
        <Space wrap>
          <ButtonBack target="/support/project" />
          {can("support_project", "delete") && p.status !== "ยกเลิก" && (
            <Button danger icon={<StopOutlined />} onClick={cancelProject}>
              ยกเลิกโปรเจ็กต์
            </Button>
          )}
          {can("support_project", "edit") && (
            <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/support/project/manage/${code}`)}>
              แก้ไข
            </Button>
          )}
        </Space>
      </Flex>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card className="ak-card" title="ข้อมูลโปรเจ็กต์">
            <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
              <Descriptions.Item label="ลูกค้า">{[p.prename, p.cus_name].filter(Boolean).join(" ")}</Descriptions.Item>
              <Descriptions.Item label="ติดต่อ">{[p.cus_tel, p.cus_line_id && `LINE ${p.cus_line_id}`].filter(Boolean).join(" · ") || "-"}</Descriptions.Item>
              <Descriptions.Item label="แบบบ้าน">{p.model_code ? `${p.model_code} ${p.model_name || ""}` : "-"}</Descriptions.Item>
              <Descriptions.Item label="พื้นที่ใช้สอย">{p.usable_area ? `${formatMoney(p.usable_area, 0)} ตร.ม.` : "-"}</Descriptions.Item>
              <Descriptions.Item label="ที่ดิน" span={2}>
                {site || "-"}
                {p.map_url && (
                  <Typography.Link href={p.map_url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8 }}>
                    <EnvironmentOutlined /> แผนที่
                  </Typography.Link>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="โฉนด">{p.land_deed_no || "-"}</Descriptions.Item>
              <Descriptions.Item label="ขนาดที่ดิน">{p.land_area || "-"}</Descriptions.Item>
              <Descriptions.Item label="เลขที่สัญญา">{p.contract_no || "-"}</Descriptions.Item>
              <Descriptions.Item label="วันที่ทำสัญญา">{d(p.contract_date)}</Descriptions.Item>
              <Descriptions.Item label="มูลค่าสัญญา">{p.contract_value ? `${formatMoney(p.contract_value, 2)} บาท` : "-"}</Descriptions.Item>
              <Descriptions.Item label="แผนงาน">{p.plan_days ? `${p.plan_days} วัน` : "-"}</Descriptions.Item>
              <Descriptions.Item label="เริ่มงาน">{d(p.start_date)}</Descriptions.Item>
              <Descriptions.Item label="สิ้นสุดตามแผน">{d(p.end_date)}</Descriptions.Item>
              {p.remark && <Descriptions.Item label="หมายเหตุ" span={2} style={{ whiteSpace: "pre-wrap" }}>{p.remark}</Descriptions.Item>}
            </Descriptions>
          </Card>
          <ProjectFiles projectCode={code} />
          </Space>
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card className="ak-card" title="ระยะเวลาตามแผน">
              {timePct === null ? (
                <Typography.Text type="secondary">ยังไม่ได้กำหนดวันเริ่มงาน</Typography.Text>
              ) : (
                <>
                  <Progress percent={timePct} strokeColor="#9b6420" />
                  <Typography.Text type="secondary">{dayText}</Typography.Text>
                </>
              )}
            </Card>

            <Card className="ak-card" title="ทีมผู้รับผิดชอบ">
              {(p.team || []).length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="ยังไม่มีทีม" />
              ) : (
                <List
                  size="small"
                  dataSource={p.team}
                  renderItem={(t) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar src={t.photo_path ? fileUrl(t.photo_path) : undefined} icon={<UserOutlined />} />}
                        title={
                          <Space size={4} wrap>
                            <span>
                              {t.firstname} {t.lastname}
                              {t.nickname ? ` (${t.nickname})` : ""}
                            </span>
                            {t.is_lead === "Y" && <Tag color="gold">หลัก</Tag>}
                            {t.active_status === "N" && <Tag color="red">ไม่ได้ทำงานแล้ว</Tag>}
                          </Space>
                        }
                        description={
                          <span style={{ fontSize: 12 }}>
                            {t.team_role}
                            {t.position && t.position !== t.team_role ? ` · ${t.position}` : ""}
                            {t.tel ? (
                              <>
                                {" · "}
                                <a href={`tel:${t.tel}`}>{t.tel}</a>
                              </>
                            ) : null}
                          </span>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>

            <ProjectRc projectCode={code} disabled={p.status === "ยกเลิก"} />
          </Space>
        </Col>

        <Col span={24}>
          <Card
            className="ak-card"
            title="ใบเลือกวัสดุ"
            extra={
              can("support_selection", "add") &&
              !activeSel &&
              p.status !== "ยกเลิก" && (
                <Button type="primary" icon={<PlusOutlined />} onClick={openNewSelection}>
                  เริ่มเลือกวัสดุ
                </Button>
              )
            }
          >
            {(p.selections || []).length === 0 ? (
              <Empty description="ยังไม่ได้เริ่มเลือกวัสดุ — กด 'เริ่มเลือกวัสดุ' แล้วเลือก Template ของแบบบ้านนี้" />
            ) : (
              <List
                dataSource={p.selections}
                renderItem={(s) => (
                  <List.Item
                    actions={[
                      <Button key="o" icon={<FormOutlined />} onClick={() => navigate(`/support/selection/manage/${s.sel_code}`)}>
                        เปิดใบเลือก
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          {s.sel_code} <StatusTag value={s.status} />
                        </Space>
                      }
                      description={`${s.tpl_name || "-"} · ${s.item_count} จุด · อัปเกรดรวม ${formatMoney(s.total_upgrade, 2)} บาท${s.locked_date ? ` · ล็อกเมื่อ ${dayjs(s.locked_date).format("DD/MM/YYYY HH:mm")}` : ""}`}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal open={openTpl} title="เริ่มใบเลือกวัสดุ" onCancel={() => setOpenTpl(false)} onOk={createSelection} confirmLoading={creating} okText="สร้างใบเลือกวัสดุ" cancelText="ยกเลิก">
        {templates.length === 0 ? (
          <Alert type="warning" showIcon message="ยังไม่มี Template ที่ใช้งานได้ — สร้างที่เมนู Master > Template เลือกวัสดุ ก่อน" />
        ) : (
          <>
            <Typography.Paragraph type="secondary">ระบบจะสร้างรายการตาม Template และเลือกวัสดุมาตรฐานไว้ให้ก่อน แล้วค่อยพาลูกค้าเปลี่ยนเป็นตัวอัปเกรด</Typography.Paragraph>
            <Select
              value={tpl}
              onChange={setTpl}
              style={{ width: "100%" }}
              options={templates.map((t) => ({
                value: t.tpl_code,
                label: `${t.tpl_name} (${t.item_count} จุด)${t.model_code ? ` · แบบ ${t.model_code}` : ""}${t.model_code && t.model_code === p.model_code ? " ★ ตรงกับแบบบ้าน" : ""}`,
              }))}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
